import type {
  DividendsListDTO,
  TransactionsDTO,
  DraftStateDTO,
  HistoryDTO,
  LeaderboardDTO,
  ManagerDetailDTO,
  MoversDTO,
  SlotsOverviewDTO,
  UniverseAllDTO,
  UniverseSearchDTO,
} from "../../shared/types";

export interface ConfigDTO {
  seasonName: string;
  numManagers: number;
  numRounds: number;
  positionsPerManager: number;
  managers: string[];
  round1Order: string[];
  duplicateRule: string;
  draftPollSec: number;
  leaderboardPollSec: number;
  totalPicks: number;
  marketHours: { open: string; close: string; tz: string };
  rounds: { round: number; order: string[] }[];
}

const ADMIN_KEY = "commish_pw";

export function getAdminPassword(): string | null {
  try {
    return sessionStorage.getItem(ADMIN_KEY);
  } catch {
    return null;
  }
}

export function setAdminPassword(pw: string | null): void {
  try {
    if (pw) sessionStorage.setItem(ADMIN_KEY, pw);
    else sessionStorage.removeItem(ADMIN_KEY);
  } catch {
    /* ignore */
  }
}


async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const pw = getAdminPassword();
  if (pw) headers.set("x-commish-password", pw);
  if (init?.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const res = await fetch(path, { ...init, headers, credentials: "include" });
  const text = await res.text();
  // The body is normally JSON, but a rate-limited / down edge can return an
  // HTML error page (e.g. Cloudflare 1027). Never blow up on JSON.parse — fall
  // back to a clean, status-aware message so the UI shows something sensible
  // and keeps polling until the API recovers.
  let data: { error?: string } | null = null;
  try {
    data = text ? (JSON.parse(text) as { error?: string }) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    if (res.status === 429) {
      throw new Error("Too much traffic right now — this will refresh on its own shortly.");
    }
    if (data?.error) throw new Error(data.error);
    throw new Error(res.status >= 500 ? "Server error — retrying…" : `HTTP ${res.status}`);
  }
  return data as unknown as T;
}

export const api = {
  config: () => req<ConfigDTO>("/api/config"),
  leaderboard: () => req<LeaderboardDTO>("/api/leaderboard"),
  history: () => req<HistoryDTO>("/api/history"),
  movers: () => req<MoversDTO>("/api/movers"),
  manager: (name: string) =>
    req<ManagerDetailDTO>(`/api/manager/${encodeURIComponent(name)}`),
  draftState: () => req<DraftStateDTO>("/api/draft/state"),
  slots: () => req<SlotsOverviewDTO>("/api/slots"),
  universe: (q: string) =>
    req<UniverseSearchDTO>(`/api/universe?q=${encodeURIComponent(q)}`),
  universeAll: () => req<UniverseAllDTO>("/api/universe/all"),

  // ---- admin ----
  session: () => req<{ authed: boolean }>("/api/admin/session"),
  login: (password: string) =>
    req<{ authed: boolean }>("/api/admin/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  logout: () => req<{ authed: boolean }>("/api/admin/logout", { method: "POST" }),

  quote: (ticker: string) =>
    req<{ ticker: string; price: number | null }>(
      `/api/admin/quote?ticker=${encodeURIComponent(ticker)}`,
    ),

  pick: (body: { ticker: string; direction: string; price?: number }) =>
    req<DraftStateDTO>("/api/draft/pick", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  undo: () => req<DraftStateDTO>("/api/draft/undo", { method: "POST" }),
  draftControl: (body: {
    action: "start" | "setStatus" | "setPick" | "reset" | "resetClock";
    status?: "pre" | "live" | "done";
    pick?: number;
  }) =>
    req<DraftStateDTO>("/api/draft/control", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  trade: (body: {
    fromSlotId: number;
    toSlotId: number;
    ticker?: string;
    price?: number;
    priceFrom?: number;
    priceTo?: number;
    note?: string;
  }) => req<{ ok: true }>("/api/trade", { method: "POST", body: JSON.stringify(body) }),

  rebalance: (body: {
    managerId: number;
    discards: number[];
    pickups: { slotId: number; ticker: string; direction: string }[];
    note?: string;
  }) =>
    req<{ ok: true }>("/api/rebalance", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  corporateAction: (body: {
    type: "mna" | "delisting" | "spinoff";
    slotId: number;
    dealPrice?: number;
    replacementTicker?: string;
    replacementDirection?: string;
    replacementPrice?: number;
    spinoffValuePerShare?: number;
    note?: string;
  }) =>
    req<{ ok: true }>("/api/corporate-action", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  overridePrice: (ticker: string, price: number) =>
    req<{ ok: true }>("/api/admin/price", {
      method: "POST",
      body: JSON.stringify({ ticker, price }),
    }),

  recompute: () => req<LeaderboardDTO>("/api/admin/recompute", { method: "POST" }),

  providerCheck: () =>
    req<{
      quotes: {
        ok: boolean;
        sample?: { symbol: string; price: number | null }[];
        error?: string;
      } | null;
      dividends: {
        ok: boolean;
        count?: number;
        sample?: { exDate: string; amount: number }[];
        error?: string;
      } | null;
    }>("/api/admin/provider-check"),

  testAlert: () =>
    req<{ ok: boolean; error?: string }>("/api/admin/test-alert", { method: "POST" }),

  audit: () =>
    req<{
      lastAudit: string | null;
      state: {
        date: string;
        checked: number;
        findings: { ticker: string; kind: string; detail: string }[];
      } | null;
    }>("/api/admin/audit"),

  transactions: () => req<TransactionsDTO>("/api/transactions"),

  dividendStatus: () =>
    req<{
      lastPass: string | null;
      tickers: Record<
        string,
        { checked: string; source: "yahoo" | "fmp" | null; count: number; error?: string }
      >;
    }>("/api/admin/dividend-status"),

  dividends: (ticker: string) =>
    req<DividendsListDTO>(`/api/dividends?ticker=${encodeURIComponent(ticker)}`),
  addDividend: (ticker: string, exDate: string, amount: number) =>
    req<{ ok: true }>("/api/dividend", {
      method: "POST",
      body: JSON.stringify({ ticker, exDate, amount }),
    }),
  removeDividend: (ticker: string, exDate: string) =>
    req<{ ok: true }>("/api/dividend/remove", {
      method: "POST",
      body: JSON.stringify({ ticker, exDate }),
    }),

  reloadUniverse: (csv: string, replace: boolean) =>
    req<{ count: number }>("/api/universe/reload", {
      method: "POST",
      body: JSON.stringify({ csv, replace }),
    }),

  addUniverseName: (ticker: string, name: string, sector: string) =>
    req<{ ticker: string }>("/api/universe/add", {
      method: "POST",
      body: JSON.stringify({ ticker, name, sector }),
    }),

  editPick: (body: {
    slotId: number;
    ticker: string;
    direction: string;
    price?: number;
  }) =>
    req<DraftStateDTO>("/api/draft/edit-pick", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  removePick: (slotId: number) =>
    req<DraftStateDTO>("/api/draft/remove-pick", {
      method: "POST",
      body: JSON.stringify({ slotId }),
    }),
};
